import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Explicit } from '../../components/ui/Explicit';
import { DrawerLink } from '../../app/DrawerLink';
import { Link, useNavigate } from 'react-router-dom';

import { Avatar } from '../../components/ui/Avatar';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { api } from '../../lib/api';
import { usePlayer } from '../../stores/player';
import { useSession } from '../../stores/session';
import { Artwork } from './Artwork';
import { duration, since, type DashboardTrack } from './api';

/**
 * The one track the page leads with.
 *
 * ## Why this slot is worth a whole row
 *
 * A grid of twelve equal cards asks somebody to choose, and the honest response
 * to being asked to choose between twelve unknown things is to choose none of
 * them. One track, large, with its artist's own words underneath, is a
 * recommendation - and a recommendation is the thing a discovery site is for.
 *
 * `DashboardRepository::featured()` picks it by recent signal against a *low*
 * follower count, so this is the opposite of the charts: not what everybody is
 * already hearing, but what a few people have found in the last two days.
 *
 * ## The blurb is the artist's, not ours
 *
 * The design mockup has editorial prose here ("Four tracks recorded on the last
 * bus home"). Nobody writes that copy on a site this size, and generating it
 * would be inventing a claim about somebody's record. So it is the description
 * they wrote, and when they wrote none there is no paragraph.
 */
export function FeaturedHero({ track }: { track: DashboardTrack }) {
  const play = usePlayer((state) => state.play);
  const current = usePlayer((state) => state.track);
  const playing = usePlayer((state) => state.playing);
  const toggle = usePlayer((state) => state.toggle);

  const isCurrent = current?.id === track.id;

  return (
    <Card className="relative flex flex-col gap-6 overflow-hidden p-7 md:p-8">
      {/*
        A wash behind the hero so it reads as a feature rather than as the first
        of a list. Pointer-events off and aria-hidden: it is a texture, and it
        must not swallow a click meant for the play button underneath.
      */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-70"
        style={{
          background:
            'radial-gradient(120% 100% at 0% 0%, color-mix(in oklab, var(--color-signal) 12%, transparent) 0%, transparent 55%)',
        }}
      />

      <div className="relative flex items-center gap-2">
        <span className="h-1.5 w-1.5 rounded-full bg-fresh" aria-hidden />
        <span className="meta-caps text-fresh">Dropped {since(track.created_at)}</span>
      </div>

      <div className="relative flex flex-col gap-6 sm:flex-row sm:items-start">
        <Artwork
          track={track}
          className="h-40 w-40 shrink-0 rounded-[var(--radius-panel)] sm:h-44 sm:w-44"
        />

        <div className="flex min-w-0 flex-col gap-3">
          <h2 className="text-3xl font-semibold tracking-tight text-ink sm:text-4xl">
            <DrawerLink to={`/tracks/${track.id}`} className="hover:underline">
              {track.explicit && <Explicit className="mr-1.5 align-[-2px]" />}
              {track.title}
            </DrawerLink>
          </h2>

          <div className="flex flex-wrap items-center gap-2 text-sm text-soft">
            <Link
              to={`/${track.owner.username}`}
              className="flex items-center gap-2 hover:text-ink"
            >
              <Avatar src={track.owner.avatar} name={track.owner.display_name} size={22} />
              {track.owner.display_name}
            </Link>

            {/* Only when there is somebody to count. "0 listeners follow them"
                is a sentence that talks somebody out of pressing play. */}
            {track.owner.follower_count !== null && track.owner.follower_count > 0 && (
              <>
                <span aria-hidden className="text-ghost">
                  ·
                </span>
                <span>
                  {track.owner.follower_count.toLocaleString()}{' '}
                  {track.owner.follower_count === 1 ? 'listener follows' : 'listeners follow'} them
                </span>
              </>
            )}
          </div>

          {track.description && (
            <p className="max-w-prose text-[15px] leading-relaxed text-soft">
              {/* Clamped rather than truncated server-side: somebody who wrote
                  four paragraphs still has them on the track's own page. */}
              <span className="line-clamp-3">{track.description}</span>
            </p>
          )}

          <div className="mt-1 flex flex-wrap items-center gap-3">
            <Button
              onClick={() => (isCurrent ? toggle() : void play(track, [track], 'dashboard'))}
              data-featured-play
            >
              {isCurrent && playing ? 'Pause' : 'Play now'}
            </Button>

            <FollowArtist username={track.owner.username} />

            <span className="font-mono text-sm text-faint">{duration(track.duration_ms)}</span>
          </div>
        </div>
      </div>
    </Card>
  );
}

/**
 * Follow, from the hero.
 *
 * A deliberately thin thing, not `FollowButton` from the profile feature: that
 * one is bound to the profile query's cache and does its optimistic update
 * against a `Profile` object this page does not have. Sharing it would mean
 * either fetching a profile the hero does not need, or teaching it a second
 * cache shape - and the second is how a component ends up with three code paths
 * and a bug in the one nobody uses.
 *
 * Hidden entirely on your own track, because following yourself is not a thing
 * the API allows and a disabled button invites the question.
 *
 * Shown to a signed-out visitor, and sends them to sign in rather than being
 * hidden. The ask lands at the moment somebody wants to do the thing, which is
 * the only moment it is welcome - and a hero with one button reads as a page
 * that does less than it does. Same behaviour as the heart on every card.
 */
function FollowArtist({ username }: { username: string }) {
  const me = useSession((state) => state.user);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const follow = useMutation({
    mutationFn: () => api.post(`/users/${username}/follow`),
    onSuccess: () => {
      /*
       * The whole dashboard, not just this button.
       *
       * Following somebody changes the "from your follows" tab, removes them
       * from the artist rail, and can change which tracks carry the "people you
       * follow saved this" tag. Refetching only the hero would leave the rest
       * of the page contradicting it.
       */
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });

  if (me?.username === username) return null;

  return (
    <Button
      variant="secondary"
      loading={follow.isPending}
      disabled={follow.isSuccess}
      onClick={() => (me ? follow.mutate() : navigate('/login'))}
      data-featured-follow
    >
      {follow.isSuccess ? 'Following' : 'Follow artist'}
    </Button>
  );
}
